import invariant from 'invariant';
import merge from 'lodash/merge';

import type { ServiceConfig } from 'Config/configs/services';
import type { FetchOptions } from 'Lib/utils/fetch';
import { getLeverValue } from 'Lib/utils/getLeverValue';

import { getProtobufSchema } from '../lib/utils/protobufUtils';
import type ApiClient from './lib/ApiClient';

export type ServiceOptions = {
  apiClient: ApiClient;
  config: ServiceConfig;
};

/**
 * Base class for Services that will contain common functions
 */
export default class Service {
  apiClient: ApiClient;
  config: ServiceConfig;
  headersOverride?: ServiceConfig['headersOverride'];

  constructor({ apiClient, config }: ServiceOptions) {
    this.config = config;
    this.apiClient = apiClient;
    this.headersOverride = config?.headersOverride;
  }

  withOverrides(options?: FetchOptions) {
    if (this.headersOverride) {
      return merge(
        {
          headers: this.headersOverride,
        },
        options
      );
    }

    return options;
  }

  /**
   * Mustache-style replace for a given path and object
   * @param {string} path - the path with mustache variables e.g. /v2/auth/login/{type}
   * @param {Object} params - the object used to replace variables in the path
   * @return {string} the resultant path with replaced variables
   */
  getUrlPath(
    path: string,
    params: {
      [x: string]: string;
    }
  ): string {
    let result: string = path;
    Object.entries(params).forEach(([key, value]: [string, string]) => {
      result = result.split(`{${key}}`).join(value);
    });
    return result;
  }

  /**
   * Looks up and returns the protobuf request schema object for a given endpoint
   * @param {string} endpoint - the endpoint name
   * @return {?Object} - the request schema object
   */
  async getRequestSchema(endpoint: string) {
    const { requestSchema } = this.config.endpoints[endpoint];
    if (!requestSchema) return null;
    return getProtobufSchema(requestSchema);
  }

  /**
   * Looks up and returns the protobuf response schema object for a given endpoint
   * @param {string} endpoint - the endpoint name
   * @return {Object} - the response schema object
   */
  async getResponseSchema(endpoint: string) {
    const { responseSchema } = this.config.endpoints[endpoint];
    invariant(responseSchema, `Response schema for ${endpoint} not found`);
    const protobufSchema = await getProtobufSchema(responseSchema);
    invariant(
      protobufSchema,
      `Protobuf path to ${responseSchema.length ? responseSchema[0] : endpoint} not found`
    );
    return protobufSchema;
  }

  /**
   * Checks if an endpoint is deprecated and needs to be replaced when the lever is on
   * @param {string} endpoint
   * @returns replacement if it's deprecated
   */
  private findReplacement(endpoint: string) {
    const { lever } = this.config.endpoints[endpoint].replacement ?? {};
    return lever && getLeverValue(lever)
      ? this.config.endpoints[endpoint].replacement
      : this.config.endpoints[endpoint];
  }

  /**
   * Returns the path for an endpoint stored in config with replaced mustache variables
   * @param {string} endpoint - the endpoint name
   * @param {Object} params - the values in which to replace variables with
   */
  getEndpoint(endpoint: string, params: any = {}): string {
    const { path } = this.findReplacement(endpoint)!;
    invariant(path, `Endpoint ${endpoint} not found`);
    return this.getUrlPath(path, params);
  }

  /**
   * Passthrough convenience method to the apiClient's fetch method
   * @param {string} path the url for the XHR request
   * @param {Object} options options to use with the XHR request
   * @param {number} timeout timeout for the request in ms
   */
  fetch(url: string, options?: FetchOptions, timeout?: number): Promise<any> {
    return this.apiClient.fetch(url, this.withOverrides(options), timeout);
  }

  /**
   * Passthrough convenience method to the apiClient's fetch method
   * @param {string} path the url for the XHR request
   * @param {Object} options options to use with the XHR request
   * @param {number} timeout timeout for the request in ms
   */
  fetchWithAuth(url: string, options?: FetchOptions, timeout?: number): Promise<any> {
    return this.apiClient.fetchWithAuth(url, this.withOverrides(options), timeout);
  }

  /**
   * Passthrough convenience method to the apiClient's fetch method
   * @param {string} path the url for the XHR request
   * @param {Object} options options to use with the XHR request
   * @param {number} timeout timeout for the request in ms
   */
  fetchWithAuthRaw(url: string, options?: FetchOptions, timeout?: number): Promise<any> {
    return this.apiClient.fetchWithAuthRaw(url, this.withOverrides(options), timeout);
  }

  /**
   * Passthrough convenience method to the apiClient's get method
   * @param {string} path the endpoint's path
   * @param {Object} data the data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  get(path: string, data?: any, options?: FetchOptions): Promise<any> {
    return this.apiClient.get(path, data, this.withOverrides(options));
  }

  /**
   * Passthrough convenience method to the apiClient's getProtobuf method
   * @param {string} path the endpoint's path
   * @param {string} endpoint the endpoint for the schema to be used
   * @param {Object} data the JSON data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  async getProtobuf(
    path: string,
    endpoint: string,
    data: any = {},
    options?: FetchOptions
  ): Promise<any> {
    const resSchema = await this.getResponseSchema(endpoint);
    return this.apiClient.getProtobuf(path, resSchema, data, this.withOverrides(options));
  }

  /**
   * Passthrough convenience method to the apiClient's put method
   * @param {string} path the endpoint's path
   * @param {Object} data the data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  put(path: string, data: any = {}, options?: FetchOptions): Promise<any> {
    return this.apiClient.put(path, data, this.withOverrides(options));
  }

  /**
   * Passthrough convenience method to the apiClient's putProtobuf method
   * @param {string} path the endpoint's path
   * @param {string} endpoint the endpoint for the schema to be used
   * @param {Object} data the JSON data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  async putProtobuf(
    path: string,
    endpoint: string,
    data: any = {},
    options?: FetchOptions
  ): Promise<any> {
    const [reqSchema, resSchema] = await Promise.all([
      this.getRequestSchema(endpoint),
      this.getResponseSchema(endpoint),
    ]);
    return this.apiClient.putProtobuf(
      path,
      reqSchema,
      resSchema,
      data,
      this.withOverrides(options)
    );
  }

  /**
   * Passthrough convenience method to the apiClient's post method
   * @param {string} path the endpoint's path
   * @param {Object} data the data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  post(path: string, data: any = {}, options?: FetchOptions): Promise<any> {
    return this.apiClient.post(path, data, this.withOverrides(options));
  }

  /**
   * Passthrough convenience method to the apiClient's postFormData method
   * @param {string} path the endpoint's path
   * @param {FormData} data the data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  postFormData(path: string, data?: FormData, options?: FetchOptions): Promise<any> {
    return this.apiClient.postFormData(path, data, this.withOverrides(options));
  }

  /**
   * Passthrough convenience method to the apiClient's postProtobuf method
   * @param {string} path the endpoint's path
   * @param {string} endpoint the endpoint for the schema to be used
   * @param {Object} data the JSON data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  async postProtobuf(
    path: string,
    endpoint: string,
    data: any = {},
    options?: FetchOptions
  ): Promise<any> {
    const [reqSchema, resSchema] = await Promise.all([
      this.getRequestSchema(endpoint),
      this.getResponseSchema(endpoint),
    ]);
    return this.apiClient.postProtobuf(
      path,
      reqSchema,
      resSchema,
      data,
      this.withOverrides(options)
    );
  }

  /**
   * Passthrough convenience method to the apiClient's delete method
   * @param {string} path the endpoint's path
   * @param {Object} data the data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  delete(path: string, data?: any, options?: FetchOptions): Promise<any> {
    return this.apiClient.delete(path, data, this.withOverrides(options));
  }

  /**
   * Passthrough convenience method to the apiClient's deleteProtobuf method
   * @param {string} path the endpoint's path
   * @param {string} endpoint the endpoint for the schema to be used
   * @param {Object} data the JSON data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  async deleteProtobuf(
    path: string,
    endpoint: string,
    data: any = {},
    options?: FetchOptions
  ): Promise<any> {
    const [reqSchema, resSchema] = await Promise.all([
      this.getRequestSchema(endpoint),
      this.getResponseSchema(endpoint),
    ]);
    return this.apiClient.deleteProtobuf(
      path,
      reqSchema,
      resSchema,
      data,
      this.withOverrides(options)
    );
  }

  /**
   * Passthrough convenience method to the apiClient's patch method
   * @param {string} path the endpoint's path
   * @param {Object} data the data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  patch(path: string, data: any = {}, options?: FetchOptions): Promise<any> {
    return this.apiClient.patch(path, data, this.withOverrides(options));
  }

  /**
   * Passthrough convenience method to the apiClient's patchProtobuf method
   * @param {string} path the endpoint's path
   * @param {string} endpoint the endpoint for the schema to be used
   * @param {Object} data the JSON data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  async patchProtobuf(
    path: string,
    endpoint: string,
    data: any = {},
    options?: FetchOptions
  ): Promise<any> {
    const [reqSchema, resSchema] = await Promise.all([
      this.getRequestSchema(endpoint),
      this.getResponseSchema(endpoint),
    ]);
    return this.apiClient.patchProtobuf(
      path,
      reqSchema,
      resSchema,
      data,
      this.withOverrides(options)
    );
  }

  /**
   * Call API based on the method defined in the configs/services
   * @param {string} endpoint the endpoint name
   * @param {Object} params the values in which to replace variables in the path
   * @param {Object} data the JSON data to pass through to the request
   * @param {Object} options optional options to use with the request api
   */
  callAPI(endpoint: string, params: any = {}, data: any = {}, options?: FetchOptions) {
    const path = this.getEndpoint(endpoint, params);
    const { method } = this.findReplacement(endpoint)!;

    const args: [string, any, FetchOptions?] = [path, data, options];
    switch (method) {
      case 'POST':
        return this.post(...args);
      case 'POST_FORM_DATA':
        return this.postFormData(...args);
      case 'PUT':
        return this.put(...args);
      case 'PATCH':
        return this.patch(...args);
      case 'DELETE':
        return this.delete(...args);
      default:
        return this.get(...args);
    }
  }
}
